import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarClock, Star, ShieldCheck } from "lucide-react";
import { company } from "@/data/site";
import { Magnetic, easeExpo } from "@/components/common/motion";

const headline = ["Empowering business", "through smart", "technology"];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-[0.12em]">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, ease: easeExpo, delay }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yBlob = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative overflow-hidden grain" data-testid="hero">
      {/* Ambient gradient field */}
      <motion.div style={{ y: yBlob }} className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -left-40 top-10 h-[38rem] w-[38rem] rounded-full bg-primary/20 blur-[130px] animate-float-slow dark:bg-primary/25" />
        <div className="absolute right-0 top-40 h-[34rem] w-[34rem] rounded-full bg-accent/20 blur-[130px] animate-float-slow dark:bg-accent/20" style={{ animationDelay: "3s" }} />
        <div className="absolute inset-0 opacity-[0.4] [background-image:linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] [background-size:64px_64px] mask-fade-b" />
      </motion.div>

      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-20 sm:px-8 lg:grid-cols-[1.1fr_0.9fr] lg:pb-28 lg:pt-28">
        {/* Left: copy */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-1.5 text-xs font-medium text-muted-foreground"
          >
            <span className="flex h-2 w-2 items-center justify-center">
              <span className="absolute h-2 w-2 animate-ping rounded-full bg-success/70" />
              <span className="h-2 w-2 rounded-full bg-success" />
            </span>
            Trusted by enterprises, SMBs & fast-growing startups
          </motion.div>

          <h1 className="mt-7 font-display text-[13vw] font-semibold leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Empowering business</MaskedLine>
            <MaskedLine delay={0.28}>through</MaskedLine>
            <MaskedLine delay={0.41}>
              <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">smart technology</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-muted-foreground"
          >
            {company.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.72 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Magnetic>
              <Link
                to="/contact"
                data-testid="hero-get-started"
                className="group inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-shadow hover:shadow-xl hover:shadow-primary/30"
              >
                Get Started
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Magnetic>
            <Link
              to="/contact"
              data-testid="hero-book-consultation"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-7 py-3.5 text-sm font-semibold transition-colors hover:border-primary/50"
            >
              <CalendarClock className="h-4 w-4 text-primary" />
              Book Consultation
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-muted-foreground"
          >
            <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /> Enterprise-grade security</span>
            <span className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
              <span className="ml-1 font-medium text-foreground">98% client satisfaction</span>
            </span>
          </motion.div>
        </div>

        {/* Right: parallax clipped image + floating cards */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: easeExpo }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-border shadow-2xl">
            <motion.img
              style={{ y: yImg, scale: scaleImg }}
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1000&q=80"
              alt="NeuroTriq engineering team collaborating"
              className="h-[30rem] w-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-secondary/50 via-transparent to-transparent" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="absolute -left-4 bottom-8 rounded-2xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur sm:-left-8"
          >
            <p className="font-display text-3xl font-semibold text-primary">50+</p>
            <p className="text-xs text-muted-foreground">Projects delivered</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.15 }}
            className="absolute -right-3 top-8 rounded-2xl border border-border bg-card/95 p-4 shadow-xl backdrop-blur sm:-right-6"
          >
            <div className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-success/15 text-success"><ShieldCheck className="h-5 w-5" /></span>
              <div>
                <p className="text-sm font-semibold">24×7 Support</p>
                <p className="text-xs text-muted-foreground">Always monitoring</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
